import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import type { InputHTMLAttributes } from 'react'

/**
 * Универсальный input с label / error / hint.
 *
 * `required` — индикатор обязательного поля. Рендерит маленький точечный маркер
 * слева от label-текста (без визуальной «*» — UX-решение по ТЗ). Сам по себе
 * `required` НЕ блокирует submit — это делает родительская форма (`error` и
 * disabled-состояние кнопки сохранения). Здесь только индикатор.
 */
export function Input({
  className,
  label,
  error,
  hint,
  required,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label?: string
  error?: string
  hint?: string
  required?: boolean
}) {
  const { t } = useTranslation()
  return (
    <label className="flex flex-col text-left">
      {label ? (
        <span className="stryzh-input-label !inline-flex items-center gap-3 leading-none">
          {required ? (
            <span
              aria-hidden
              title={t('common.required')}
              className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--stryzh-color-accent-orange)]"
            />
          ) : null}
          <span>{label}</span>
        </span>
      ) : null}
      <input
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        className={clsx(
          'stryzh-input-field',
          error && 'stryzh-input-field--error',
          className,
        )}
        {...props}
      />
      {error ? (
        <span className="stryzh-input-error-text mt-1">{error}</span>
      ) : hint ? (
        <span className="mt-1 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {hint}
        </span>
      ) : null}
    </label>
  )
}
